import { ElementRef, Injectable } from '@angular/core';
import { CdkDragDrop, CdkDragEnter, CdkDragExit, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Userstory } from '../models/userstory';
import { concat, Observable } from 'rxjs';
import { SettingsService } from './settings.service';
import { UserstoryService } from './userstory.service';
import { Task } from '../models/task';
import { SynchronizationService } from './synchronization.service';
import { Issue } from '../models/issue';

@Injectable({
  providedIn: 'root'
  })
export class SprintPlanning2Service {

  private readonly sprintId;
  private readonly sp2Id = 'sp2-issue-container';
  private readonly sp2ColumnId = 'sp2-column-view';
  private readonly pbId = 'product-backlog-issue-container';

  constructor(
    private userStoryService: UserstoryService,
    private settingsService: SettingsService,
    private sync: SynchronizationService,
  ) {
    this.sprintId = settingsService.getSprintId(settingsService.getProjectName());
  }

  /**
   *  TODO:
   *    - Move subissues inside issue (ranking) in row view
   *      Possible workaround: https://stackblitz.com/edit/angular-nuiviw?file=src%2Fapp%2Fapp.component.html
   */
  onIssueDrop(event: CdkDragDrop<Userstory[]>): void {
    const sourceContainer = event.previousContainer;
    const targetContainer = event.container;
    const projectKey = this.settingsService.getProjectName();

    // move issue from PB to SP2
    if (sourceContainer.id === this.pbId && (targetContainer.id === this.sp2Id || targetContainer.id === this.sp2ColumnId)) {
      const issue: Userstory = sourceContainer.data[event.previousIndex];
      transferArrayItem(sourceContainer.data, targetContainer.data, event.previousIndex, event.currentIndex);

      // Issues in backlog do not contain subtasks, when dropping in sprint, load issues
      this.userStoryService.getSubissues(projectKey, issue.id).subscribe(tasks => {
        targetContainer.data[event.currentIndex]['tasks'] = tasks;

        this.sendSyncEventIssueMoveEnded(event.item.data.id, 'backlog');
        this.sendSyncEventMoveIssueToSprintBacklog(targetContainer.data[event.currentIndex], event.previousIndex, event.currentIndex);
      });

      concat(
        this.userStoryService.assignIssuesToSprint(String(projectKey), this.sprintId.toString(), [issue]),
        this.userStoryService.changeRank(projectKey, issue, targetContainer.data)
      ).subscribe();
    }

    // Issue order changed in sp2-container. change rank
    if ((sourceContainer.id === targetContainer.id) && (targetContainer.id === this.sp2Id || targetContainer.id === this.sp2ColumnId)) {
      const issue: Userstory = sourceContainer.data[event.previousIndex];
      moveItemInArray(targetContainer.data, event.previousIndex, event.currentIndex);

      this.userStoryService.changeRank(projectKey, issue, targetContainer.data).subscribe();
      this.sendSyncEventIssueMoveEnded(event.item.data.id, 'sp2');
      this.sendSyncEventChangeIssueOrder('sprint-backlog', event.previousIndex, event.currentIndex);
    }
  }

  private changeDragPreview(event: any, display: string): any[] {
    const dragPreviewp2 = new ElementRef<HTMLElement>(document.querySelector('.cdk-drag-preview-sp2'));
    const dragPreviewBacklog = new ElementRef<HTMLElement>(document.querySelector('.cdk-drag-preview-pb'));
    const isSp2ContainerEvent = (event.container.id === this.sp2Id || event.container.id === this.sp2ColumnId);
    const dragPreviewElement = (isSp2ContainerEvent) ? dragPreviewp2.nativeElement : dragPreviewBacklog.nativeElement;

    return [dragPreviewElement, 'display', display];
  }

  getConnectedIssues(): any[] {
    return ['sp2-column-view', 'sp2-issue-container', 'product-backlog-issue-container'];
  }

  onSubissueDrop(event: CdkDragDrop<Userstory>): void {
    const sourceContainer = event.previousContainer;
    const targetContainer = event.container;
    const parentIssue: Userstory = targetContainer.data;
    const subissue: Task = event.item.data;
    const oldSubissueId: number = subissue.id;
    const projectKey = this.settingsService.getProjectName();

    /* TODO: JIRA API doesn't provide an endpoint to move a task.
             As a WORKAROUND the task will be cloned, attached to the new issue and the old one will be deleted.
             ATTENTION: There is a potential loss of information from data stored in JIRA but not mapped in aWall.
     */
    this.sendSyncEventIssueMoveEnded(event.item.data.id, 'sp2');

    // move subissue to new issue
    if (sourceContainer.id !== targetContainer.id) {
      // fix: calculate indices
      sourceContainer.data.tasks.forEach((t, i) => {
        if (subissue.id === t.id) {
          event.previousIndex = i;
        }
      });

      // supports ordering subissues inside an issue
      targetContainer.data.tasks.forEach((t, i) => {
        if (subissue.id === t.id) {
          event.currentIndex = i;
        }
      });

      // move subissue in data structure
      transferArrayItem(sourceContainer.data.tasks, targetContainer.data.tasks, event.previousIndex, event.currentIndex);

      // clone issue
      this.userStoryService.createSubissue(
        projectKey,
        parentIssue.id,
        subissue.summary,
        subissue.description,
        subissue.assignee,
        subissue.reporter,
        subissue.status.id
      ).subscribe(newSubissueData => {
        // update old subissue (in board)
        subissue.update(this.userStoryService.mapJSONToTask(newSubissueData, parentIssue.id,));
        this.changeTaskRank(parentIssue.id, targetContainer.data.tasks.length - 1, event.currentIndex).subscribe();

        // delete old subissue in pyWall
        this.userStoryService.deleteIssue(oldSubissueId, projectKey).subscribe(_ => {
          this.sendSyncEventMoveTaskToOtherIssue(sourceContainer.data.id, targetContainer.data.id);
        });
      });
      // move subissue inside issue (change rank)
    } else {
      // Change rank for column view
      moveItemInArray(sourceContainer.data.tasks, event.previousIndex, event.currentIndex);
      this.changeTaskRank(parentIssue.id, event.previousIndex, event.currentIndex).subscribe();
      this.sendSyncEventChangeTaskOrder(parentIssue.id, event.previousIndex, event.currentIndex);
    }
  }

  onIssueDragListEnter(event: CdkDragEnter<any>) {
    return this.changeDragPreview(event, 'block');
  }

  onIssueDragListEnterExit(event: CdkDragExit<any>) {
    return this.changeDragPreview(event, 'none');
  }

  private changeTaskRank(storyId, indexFrom, indexTo): Observable<any> {
    return this.userStoryService.changeRankOfTask(this.settingsService.getProjectName(), storyId, indexFrom, indexTo);
  }

  addIssueAtIndex(issues: Userstory[], createdIssue: Issue, index: number): void {
    if (createdIssue instanceof Userstory) {
      issues.push(createdIssue);

      moveItemInArray(issues, issues.lastIndexOf(createdIssue), index);
      const projectKey = this.settingsService.getProjectName();
      this.userStoryService.changeRank(projectKey, createdIssue, issues);
    }
  }

  private sendSyncEventMoveIssueToSprintBacklog(issue, indexFrom, indexTo): void {
    const syncData = {
      'issue': issue,
      'indexFrom': indexFrom,
      'indexTo': indexTo
    };
    this.sync.sendPersistenceEvent('moveIssueToSprintBacklog', syncData);
  }

  public sendSyncEventMoveIssueToProductBacklog(issue, indexFrom, indexTo): void {
    const syncData = {
      'issue': issue,
      'indexFrom': indexFrom,
      'indexTo': indexTo
    };
    this.sync.sendPersistenceEvent('moveIssueToProductBacklog', syncData);
  }

  public sendSyncEventChangeIssueOrder(component, indexFrom, indexTo): void {
    const syncData = {
      'component': component,
      'indexFrom': indexFrom,
      'indexTo': indexTo
    };
    this.sync.sendPersistenceEvent('changeIssueOrder', syncData);
  }

  private sendSyncEventChangeTaskOrder(issueId, indexFrom, indexTo): void {
    const syncData = {
      'issueId': issueId,
      'indexFrom': indexFrom,
      'indexTo': indexTo
    };
    this.sync.sendPersistenceEvent('changeTaskOrder', syncData);
  }

  private sendSyncEventMoveTaskToOtherIssue(issueIdFrom, issueIdTo): void {
    const syncData = {
      'issueIdFrom': issueIdFrom,
      'issueIdTo': issueIdTo
    };
    this.sync.sendPersistenceEvent('moveTaskToOtherIssue', syncData);
  }

  public sendSyncEventIssueMoveEnded(issueId: number, eventSource: string): void {
    this.sync.sendRealTimeEvent('IssueMoveEnded', { 'id': issueId, 'source': eventSource });
  }
}
